import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Site-wide buttery smooth scroll powered by Lenis + a scroll-reveal
 * IntersectionObserver bridge for `[data-reveal]` elements.
 * - Respects prefers-reduced-motion (disables smooth scroll entirely).
 * - Editorial easing tuned for calm, professional pacing.
 * - Auto-observes newly mounted reveal targets via MutationObserver.
 */
export function SmoothScroll() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let lenis: Lenis | null = null;
    let rafId = 0;

    if (!reduce) {
      lenis = new Lenis({
        duration: 1.15,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1.2,
        lerp: 0.09,
      });
      const raf = (time: number) => {
        lenis!.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
      document.documentElement.classList.add("lenis-active");
    }

    // Scroll reveal bridge
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = Number(el.dataset.revealDelay ?? 0);
            if (delay) {
              window.setTimeout(() => el.classList.add("is-inview"), delay);
            } else {
              el.classList.add("is-inview");
            }
            io.unobserve(el);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
    );

    const observeAll = (root: ParentNode = document) => {
      root.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-inview)").forEach((el) => io.observe(el));
    };
    observeAll();

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) {
            const el = n as HTMLElement;
            if (el.matches?.("[data-reveal]")) io.observe(el);
            observeAll(el);
          }
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      lenis?.destroy();
      io.disconnect();
      mo.disconnect();
      document.documentElement.classList.remove("lenis-active");
    };
  }, []);

  return null;
}

export default SmoothScroll;

